import { t } from "@lingui/macro";
import { PencilSimpleLine, Swatches } from "@phosphor-icons/react";
import { useBreakpoint } from "@reactive-resume/hooks";
import {
  type ImperativePanelHandle,
  Panel,
  PanelGroup,
  PanelResizeHandle,
  Sheet,
  SheetContent,
} from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { useEffect, useRef } from "react";
import { Outlet, useSearchParams } from "react-router-dom";

import { AiAssistant } from "@/client/components/ai-assistant";
import { GrandfatherBanner } from "@/client/components/grandfather-banner";
import { useBuilderStore } from "@/client/stores/builder";

import { BuilderHeader } from "./_components/header";
import { BuilderToolbar } from "./_components/toolbar";
import { LeftSidebar } from "./sidebars/left";
import { RightSidebar } from "./sidebars/right";

const LEFT_DEFAULT_PERCENT = 28;
const RIGHT_DEFAULT_PERCENT = 28;
const MIN_SIDEBAR_PERCENT = 18;
const LEFT_MAX_PERCENT = 40;
const RIGHT_MAX_PERCENT = 34;
const MIN_CENTER_PERCENT = 24;
/** When collapsed, sidebars fully hide (0px) for maximum resume preview space. */
const COLLAPSED_SIDEBAR_PX = 0;

const ArtboardSlot = () => (
  <div className="relative h-full">
    <Outlet />
    <BuilderToolbar />
  </div>
);

export const BuilderLayout = () => {
  const { isDesktop } = useBreakpoint();
  const [searchParams] = useSearchParams();
  const leftPanelRef = useRef<ImperativePanelHandle>(null);
  const rightPanelRef = useRef<ImperativePanelHandle>(null);

  const sheet = useBuilderStore((state) => state.sheet);
  const leftSetSize = useBuilderStore((state) => state.panel.left.setSize);
  const rightSetSize = useBuilderStore((state) => state.panel.right.setSize);
  const leftCollapsed = useBuilderStore((state) => state.panel.left.collapsed);
  const rightCollapsed = useBuilderStore((state) => state.panel.right.collapsed);
  const leftSetCollapsed = useBuilderStore((state) => state.panel.left.setCollapsed);
  const rightSetCollapsed = useBuilderStore((state) => state.panel.right.setCollapsed);
  const leftHandle = useBuilderStore((state) => state.panel.left.handle);
  const rightHandle = useBuilderStore((state) => state.panel.right.handle);
  const setLeftSheetOpen = useBuilderStore((state) => state.sheet.left.setOpen);

  const onOpenAutoFocus = (event: Event) => event.preventDefault();

  useEffect(() => {
    if (isDesktop) return;
    if (searchParams.get("onboarding") !== "1") return;
    setLeftSheetOpen(true);
  }, [isDesktop, searchParams, setLeftSheetOpen]);

  if (isDesktop) {
    return (
      <div className="flex h-screen w-full max-w-[100vw] flex-col overflow-hidden">
        <BuilderHeader />
        <div className="shrink-0 px-4 pt-2">
          <GrandfatherBanner />
        </div>

        <div className="relative min-h-0 min-w-0 w-full max-w-full flex-1 overflow-hidden">
          <PanelGroup direction="horizontal" className="h-full w-full min-w-0 max-w-full overflow-hidden" autoSaveId="builder-panels-v4">
            <Panel
              ref={leftPanelRef}
              id="left"
              order={1}
              collapsible
              collapsedSizePixels={COLLAPSED_SIDEBAR_PX}
              minSizePercentage={MIN_SIDEBAR_PERCENT}
              maxSizePercentage={LEFT_MAX_PERCENT}
              defaultSizePercentage={LEFT_DEFAULT_PERCENT}
              onResize={({ sizePercentage }) => leftSetSize(sizePercentage ?? 0)}
              onCollapse={() => leftSetCollapsed(true)}
              onExpand={() => leftSetCollapsed(false)}
              className={cn(
                "z-[60] h-full min-w-0 overflow-hidden bg-background",
                !leftHandle.isDragging && "transition-[flex]",
              )}
            >
              <LeftSidebar
                collapsed={leftCollapsed}
                onExpand={() => leftPanelRef.current?.expand()}
                onCollapse={() => leftPanelRef.current?.collapse()}
              />
            </Panel>
            <PanelResizeHandle
              collapsed={leftCollapsed}
              collapseSide="left"
              collapseLabel={t`Hide editor`}
              railIcon={<PencilSimpleLine size={18} weight="duotone" />}
              railLabel={t`Editor`}
              onExpand={() => leftPanelRef.current?.expand()}
              onCollapse={() => leftPanelRef.current?.collapse()}
              isDragging={leftHandle.isDragging}
              onDragging={leftHandle.setDragging}
              onDoubleClick={() =>
                leftPanelRef.current?.resize({ sizePercentage: LEFT_DEFAULT_PERCENT })
              }
            />
            <Panel
              id="center"
              order={2}
              minSizePercentage={MIN_CENTER_PERCENT}
              className="min-w-0 overflow-hidden"
            >
              <ArtboardSlot />
            </Panel>
            <PanelResizeHandle
              collapsed={rightCollapsed}
              collapseSide="right"
              collapseLabel={t`Hide template & style`}
              railIcon={<Swatches size={18} weight="duotone" />}
              railLabel={t`Template & style`}
              onExpand={() => rightPanelRef.current?.expand()}
              onCollapse={() => rightPanelRef.current?.collapse()}
              isDragging={rightHandle.isDragging}
              onDragging={rightHandle.setDragging}
              onDoubleClick={() =>
                rightPanelRef.current?.resize({ sizePercentage: RIGHT_DEFAULT_PERCENT })
              }
            />
            <Panel
              ref={rightPanelRef}
              id="right"
              order={3}
              collapsible
              collapsedSizePixels={COLLAPSED_SIDEBAR_PX}
              minSizePercentage={MIN_SIDEBAR_PERCENT}
              maxSizePercentage={RIGHT_MAX_PERCENT}
              defaultSizePercentage={RIGHT_DEFAULT_PERCENT}
              onResize={({ sizePercentage }) => rightSetSize(sizePercentage ?? 0)}
              onCollapse={() => rightSetCollapsed(true)}
              onExpand={() => rightSetCollapsed(false)}
              className={cn(
                "z-[60] h-full min-w-0 overflow-hidden border-l bg-secondary-accent",
                !rightHandle.isDragging && "transition-[flex]",
              )}
            >
              <RightSidebar
                collapsed={rightCollapsed}
                onExpand={() => rightPanelRef.current?.expand()}
                onCollapse={() => rightPanelRef.current?.collapse()}
              />
            </Panel>
          </PanelGroup>
        </div>

        <AiAssistant />
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col">
      <BuilderHeader />
        <div className="shrink-0 px-4 pt-2">
          <GrandfatherBanner />
        </div>

      <div className="relative min-h-0 flex-1">
        <Sheet open={sheet.left.open} onOpenChange={sheet.left.setOpen}>
          <SheetContent
            side="left"
            showClose={false}
            onOpenAutoFocus={onOpenAutoFocus}
            className="p-0 sm:max-w-xl"
          >
            <LeftSidebar />
          </SheetContent>
        </Sheet>

        <ArtboardSlot />

        <Sheet open={sheet.right.open} onOpenChange={sheet.right.setOpen}>
          <SheetContent
            side="right"
            showClose={false}
            onOpenAutoFocus={onOpenAutoFocus}
            className="p-0 sm:max-w-xl"
          >
            <RightSidebar />
          </SheetContent>
        </Sheet>
      </div>

      <AiAssistant />
    </div>
  );
};
